import React, { memo, useCallback, useRef, useState } from 'react';
import { LayoutAnimation, StyleSheet, View, } from 'react-native';
import Text from 'design-system/Text';
import Animated, { cancelAnimation, Easing, useAnimatedStyle, useSharedValue, withDelay, withRepeat, withSequence, withSpring, withTiming, } from 'react-native-reanimated';
import useColors from 'hooks/useColors';
import { Button, IconClose } from 'design-system';
import { useFocusEffect } from 'hooks';
import TouchableOpacity from 'utils/packages/TouchableOpacity';
import { CardView } from '../../../../design-system/CardView';
import { hitSlop, rem } from '../../../../design-system/values';
import AnimatedRevealContainer from '../../../../design-system/AnimatedRevealContainer';
import Triangle from '../../../../design-system/Triangle';
const ARROW_HEIGHT = rem(10.5);
const ARROW_WIDTH = rem(12.12);
const BOTTOM_MARGIN = rem(24);
const START_ANGLE = 0;
const WIND_UP_ANGLE = -1;
const WIND_UP_DURATION = 150;
const LEAN_ANGLE = 3;
const LEAN_DURATION = 150;
const LEAN_EASING = Easing.out(Easing.cubic);
const DAMPING = 8;
const STIFFNESS = 200;
const MASS = 1;
const SCALE_UP = 1.02;
const SCALE_DURATION = 200;
const AnimatedWiggleCard = ({ title, description, buttonText, onPress, hideArrow, disableFade, disableWiggle = false, delay = 0, repeatEnabled = false, repeatDelay = 1000, color, onDismiss, isFetching, cardStyle, }) => {
    const rotation = useSharedValue(START_ANGLE);
    const scale = useSharedValue(1);
    const skipFocusAnimation = useRef(false);
    useFocusEffect(useCallback(() => {
        if (disableWiggle)
            return undefined;
        if (skipFocusAnimation.current) {
            skipFocusAnimation.current = false;
            return undefined;
        }
        const wiggle = withSequence(withTiming(WIND_UP_ANGLE, { duration: WIND_UP_DURATION }), withTiming(LEAN_ANGLE, {
            duration: LEAN_DURATION,
            easing: LEAN_EASING,
        }), withSpring(START_ANGLE, {
            damping: DAMPING,
            stiffness: STIFFNESS,
            mass: MASS,
        }), ...(repeatEnabled
            ? [withDelay(repeatDelay, withTiming(0, { duration: 0 }))]
            : []));
        const scalePulse = withSequence(withTiming(SCALE_UP, { duration: SCALE_DURATION }), withTiming(1, { duration: SCALE_DURATION }), ...(repeatEnabled
            ? [withDelay(repeatDelay, withTiming(1, { duration: 0 }))]
            : []));
        rotation.value = withDelay(delay, repeatEnabled ? withRepeat(wiggle, -1) : wiggle);
        scale.value = withDelay(delay, repeatEnabled ? withRepeat(scalePulse, -1) : scalePulse);
        return () => {
            cancelAnimation(rotation);
            cancelAnimation(scale);
        };
    }, [delay, disableWiggle, repeatDelay, repeatEnabled, rotation, scale]));
    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ rotate: `${rotation.value}deg` }, { scale: scale.value }],
    }));
    const colors = useColors();
    const [isDismissed, setIsDismissed] = useState(false);
    const handleDismiss = useCallback(() => {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        setIsDismissed(true);
        setTimeout(() => onDismiss?.(), 800);
    }, [onDismiss]);
    const handlePress = useCallback(() => {
        skipFocusAnimation.current = true;
        onPress?.();
    }, [onPress]);
    return (<View style={[
            styles.container,
            disableFade && styles.disableFade,
            {
                height: isDismissed ? 0.01 : 'auto',
                overflow: isDismissed ? 'hidden' : 'visible',
            },
        ]}>
      <AnimatedRevealContainer delay={500} extraHeight={BOTTOM_MARGIN} startingHeight={BOTTOM_MARGIN} disableAnimate={disableFade}>
        <Animated.View style={animatedStyle}>
          {!hideArrow && (<Triangle width={ARROW_WIDTH} height={ARROW_HEIGHT} color={colors.cards.onDark} style={styles.triangle}/>)}
          <CardView style={[
            styles.card,
            !hideArrow && styles.arrowExtraMargin,
            cardStyle,
        ]} noMarginBottom>
            {!!onDismiss && (<TouchableOpacity hoverOnDark onPress={handleDismiss} style={styles.cancelButton} hitSlop={hitSlop}>
                <IconClose alt color={colors.elements.secondary}/>
              </TouchableOpacity>)}
            <Text Text-16>{title}</Text>
            <Text TextThin-14 Secondary>
              {description}
            </Text>
            {!!buttonText && !!onPress && (<Button {...color} square isFetching={isFetching} title={buttonText} onPress={handlePress} containerStyle={styles.button}/>)}
          </CardView>
        </Animated.View>
      </AnimatedRevealContainer>
    </View>);
};
const styles = StyleSheet.create({
    container: {
        marginBottom: -BOTTOM_MARGIN,
    },
    disableFade: {
        marginBottom: 0,
    },
    card: {
        padding: rem(16),
        gap: rem(4),
    },
    arrowExtraMargin: {
        marginTop: rem(16) + ARROW_HEIGHT,
    },
    triangle: {
        position: 'absolute',
        top: rem(16),
        left: '50%',
        transform: [{ translateX: -ARROW_WIDTH / 2 }],
    },
    button: {
        marginTop: rem(16),
    },
    cancelButton: {
        position: 'absolute',
        top: rem(8),
        right: rem(8),
        width: rem(24),
        height: rem(24),
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1,
    },
});
export default memo(AnimatedWiggleCard);
